/**
 * Container for the range filter field component
 *
 * Manages bidirectional sync between free-form text inputs and a
 * dual-thumb slider. Clamping and min-le-max enforcement happen on
 * blur so the user can type freely without interruption.
 * @module features/scouting/components/RangeFilterField/RangeFilterFieldContainer
 */

import { useCallback, useEffect, useState } from "react";

import RangeFilterFieldView from "./RangeFilterFieldView";

/**
 * Default display formatter — plain numeric string with no formatting.
 * @param value - The numeric value
 * @returns Plain numeric string for the text input
 */
const defaultFormatDisplayValue = (value: number): string => String(value);

/** Props for RangeFilterFieldContainer */
interface RangeFilterFieldContainerProps {
  /** Filter label displayed above the inputs */
  readonly label: string;
  /** Current [min, max] range value */
  readonly value: [number, number];
  /** Lowest allowed value */
  readonly minValue: number;
  /** Highest allowed value */
  readonly maxValue: number;
  /** Step increment for the slider */
  readonly step: number;
  /** Called when the range changes (on blur or slider move) */
  readonly onChange: (value: [number, number]) => void;
  /** Whether values represent currency (forwarded to slider) */
  readonly isCurrency?: boolean;
  /** Whether to use logarithmic scale (forwarded to slider) */
  readonly useLogScale?: boolean;
  /** Optional formatter for display values in text inputs (e.g. comma formatting) */
  readonly formatDisplayValue?: (value: number) => string;
  /** Whether dark mode is active — for portal-safe styling */
  readonly isDark?: boolean;
  /** Optional handle on the field root; see the view's prop docs. */
  readonly testID?: string;
}

/**
 * Manages text/slider synchronization and value clamping for a range filter
 * @param root0 - Component props
 * @param root0.testID - Optional handle on the field root
 * @param root0.label - Filter label
 * @param root0.value - Current [min, max] range
 * @param root0.minValue - Lowest allowed value
 * @param root0.maxValue - Highest allowed value
 * @param root0.step - Slider step increment
 * @param root0.onChange - Range change callback
 * @param root0.isCurrency - Currency formatting flag
 * @param root0.useLogScale - Log scale flag
 * @param root0.formatDisplayValue - Optional formatter for display values in text inputs
 * @param root0.isDark - Whether dark mode is active
 */
const RangeFilterFieldContainer = ({
  label,
  value,
  minValue,
  maxValue,
  step,
  onChange,
  isCurrency,
  useLogScale,
  formatDisplayValue = defaultFormatDisplayValue,
  isDark,
  testID,
}: RangeFilterFieldContainerProps) => {
  const [range, setRange] = useState<[number, number]>(value);
  const [minText, setMinText] = useState(formatDisplayValue(value[0]));
  const [maxText, setMaxText] = useState(formatDisplayValue(value[1]));

  const handleSliderChange = useCallback(
    (newValue: [number, number]) => {
      setRange(newValue);
      setMinText(formatDisplayValue(newValue[0]));
      setMaxText(formatDisplayValue(newValue[1]));
      onChange(newValue);
    },
    [formatDisplayValue, onChange]
  );

  const handleMinBlur = useCallback(() => {
    const stripped = minText.replace(/[^0-9.]/g, "");
    const parsed = Number(stripped);
    const safe = Number.isNaN(parsed) ? minValue : parsed;
    const clamped = Math.max(minValue, Math.min(safe, range[1]));
    const next: [number, number] = [clamped, range[1]];
    setRange(next);
    setMinText(formatDisplayValue(clamped));
    onChange(next);
  }, [formatDisplayValue, minText, minValue, range, onChange]);

  const handleMaxBlur = useCallback(() => {
    const stripped = maxText.replace(/[^0-9.]/g, "");
    const parsed = Number(stripped);
    const safe = Number.isNaN(parsed) ? maxValue : parsed;
    const clamped = Math.min(maxValue, Math.max(safe, range[0]));
    const next: [number, number] = [range[0], clamped];
    setRange(next);
    setMaxText(formatDisplayValue(clamped));
    onChange(next);
  }, [formatDisplayValue, maxText, maxValue, range, onChange]);

  /** Strips non-numeric chars while typing and updates range immediately */
  const handleMinTextChange = useCallback(
    (text: string) => {
      const clean = text.replace(/[^0-9.]/g, "");
      setMinText(clean);
      const parsed = Number(clean);
      if (!Number.isNaN(parsed) && clean !== "") {
        const clamped = Math.max(minValue, Math.min(parsed, range[1]));
        const next: [number, number] = [clamped, range[1]];
        setRange(next);
        onChange(next);
      }
    },
    [minValue, range, onChange]
  );
  const handleMaxTextChange = useCallback(
    (text: string) => {
      const clean = text.replace(/[^0-9.]/g, "");
      setMaxText(clean);
      const parsed = Number(clean);
      if (!Number.isNaN(parsed) && clean !== "") {
        const clamped = Math.min(maxValue, Math.max(parsed, range[0]));
        const next: [number, number] = [range[0], clamped];
        setRange(next);
        onChange(next);
      }
    },
    [maxValue, range, onChange]
  );

  // Sync internal state when the parent resets the value (e.g. "Clear All")
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- External prop sync for uncontrolled component
    setRange(value);

    setMinText(formatDisplayValue(value[0]));

    setMaxText(formatDisplayValue(value[1]));
  }, [value[0], value[1]]); // eslint-disable-line react-hooks/exhaustive-deps -- Compare individual tuple values, not reference

  return (
    <RangeFilterFieldView
      label={label}
      minText={minText}
      maxText={maxText}
      onMinTextChange={handleMinTextChange}
      onMaxTextChange={handleMaxTextChange}
      onMinBlur={handleMinBlur}
      onMaxBlur={handleMaxBlur}
      sliderValue={range}
      sliderMinValue={minValue}
      sliderMaxValue={maxValue}
      sliderStep={step}
      onSliderChange={handleSliderChange}
      isCurrency={isCurrency}
      useLogScale={useLogScale}
      isDark={isDark}
      testID={testID}
    />
  );
};

export default RangeFilterFieldContainer;
